/**
 * An agent's ROUTE TREE — the screens its face can push to without leaving the
 * frame.
 *
 * A face is one screen. Every agent whose work has DEPTH (a quiz run under the
 * learn board, a curriculum path under it) used to reach that depth by leaving:
 * in the frame there was nowhere to push, so the board fell back to
 * `window.location.assign(<subdomain>/run/…)` and the nurse landed in a
 * full-page sub-app with its own header, outside the twin. The screen she
 * asked for was right, the frame around it was gone.
 *
 * So an agent declares its depth ONCE, as data, and both surfaces mount the
 * same table: `apps/<slug>/src/App.tsx` under its BrowserRouter (full-screen)
 * and AgentFrame under a pane-local router (in-frame). Paths are absolute and
 * identical on both, so `navigate('/run/x/1')` from the board means the same
 * thing on either surface — which is what lets the link-out fallback be
 * deleted rather than duplicated.
 *
 * DISCOVERED, NOT REGISTERED — the same convention as the face beside it:
 * `apps/<slug>/src/routes.tsx`, keyed on the directory name. An agent that has
 * no depth declares no module and nothing here changes; it renders exactly as
 * before, with no router wrapped around it.
 *
 * ADDING DEPTH TO AN AGENT: create `apps/<slug>/src/routes.tsx` exporting
 * `routes`, and have its App.tsx mount the same array. Nothing in the platform
 * changes. If one of the agent's SESSION KINDS should open on one of those
 * screens rather than on its face, say so in `sessionEntry.ts` beside it —
 * that answer is deliberately in a page-free module (see agentSessionEntry.ts).
 */
import type { ReactElement } from 'react'

import { agentSlugForDir, slugFromAppModulePath } from './appModulePath'

/** One screen in an agent's route tree. `path` is absolute (leading `/`). */
export interface AgentRoute {
  path: string
  element: ReactElement
}

export interface AgentRoutesModule {
  routes: AgentRoute[]
}

// Vite requires a LITERAL glob — the pattern IS the convention. Eager over the
// route MODULES (a handful of route descriptors), while each agent lazy-loads
// the SCREENS behind them: the main app bundles every face, so eager screens
// would put every agent's depth in every nurse's first paint.
const DISCOVERED_ROUTES = import.meta.glob<AgentRoutesModule>('../../../../apps/*/src/routes.tsx', {
  eager: true,
})

// Keyed on the agent's DECLARED slug, not the directory — see
// appModulePath.ts's agentSlugForDir.
export const AGENT_ROUTES: Record<string, AgentRoutesModule> = Object.fromEntries(
  Object.entries(DISCOVERED_ROUTES).map(([path, mod]) => [
    agentSlugForDir(slugFromAppModulePath(path, 'routes.tsx')),
    mod,
  ]),
)

/** An agent's route tree, or null when it has no depth beyond its face. */
export function getAgentRoutes(agentSlug: string | undefined): AgentRoutesModule | null {
  if (!agentSlug) return null
  return AGENT_ROUTES[agentSlug] ?? null
}

